/** Clinical catalogs — templates, ICD-10, drugs, labs */

export const EMR_TABS = [
  { id: 'consult', label: 'Consult' },
  { id: 'history', label: 'History' },
  { id: 'vitals', label: 'Vitals & Exam' },
  { id: 'assessment', label: 'Assessment' },
  { id: 'rx', label: 'Prescription' },
  { id: 'labs', label: 'Investigations' },
  { id: 'plan', label: 'Plan & Advice' },
  { id: 'notes', label: 'SOAP Notes' },
];

export const COMPLAINT_TEMPLATES = [
  { label: 'Fever & cough', text: 'Fever with cough since 3 days, no breathlessness.' },
  { label: 'HTN follow-up', text: 'Known hypertensive on treatment, for routine follow-up.' },
  { label: 'Diabetes review', text: 'Known DM on OHA, for sugar control review.' },
  { label: 'Gastritis', text: 'Epigastric burning, nausea, worse after meals.' },
  { label: 'Body ache', text: 'Generalised body ache and weakness.' },
  { label: 'Skin rash', text: 'Itchy erythematous rash over trunk and limbs.' },
];

export const FOOD_OPTIONS = ['After food', 'Before food', 'With food', 'Empty stomach', 'At bedtime'];

export const RX_TEMPLATES = {
  uri: [
    { drug: 'Paracetamol 650mg', dose: '1 tab', m: 1, a: 1, n: 1, food: 'After food', duration: '3 days', route: 'Oral', instructions: 'If fever' },
    { drug: 'Levocetirizine 5mg', dose: '1 tab', m: 0, a: 0, n: 1, food: 'At bedtime', duration: '5 days', route: 'Oral', instructions: '' },
    { drug: 'Ambroxol 30mg', dose: '1 tab', m: 1, a: 0, n: 1, food: 'After food', duration: '5 days', route: 'Oral', instructions: '' },
  ],
  gastritis: [
    { drug: 'Pantoprazole 40mg', dose: '1 tab', m: 1, a: 0, n: 0, food: 'Before food', duration: '14 days', route: 'Oral', instructions: 'Before breakfast' },
    { drug: 'Sucralfate 1g', dose: '1 tab', m: 1, a: 1, n: 1, food: 'Before food', duration: '7 days', route: 'Oral', instructions: '' },
  ],
  hypertension: [
    { drug: 'Amlodipine 5mg', dose: '1 tab', m: 1, a: 0, n: 0, food: 'After food', duration: '30 days', route: 'Oral', instructions: 'Morning' },
    { drug: 'Telmisartan 40mg', dose: '1 tab', m: 1, a: 0, n: 0, food: 'After food', duration: '30 days', route: 'Oral', instructions: 'Morning' },
  ],
  diabetes: [
    { drug: 'Metformin 500mg', dose: '1 tab', m: 1, a: 0, n: 1, food: 'After food', duration: '30 days', route: 'Oral', instructions: '' },
    { drug: 'Glimepiride 1mg', dose: '1 tab', m: 1, a: 0, n: 0, food: 'Before food', duration: '30 days', route: 'Oral', instructions: 'Before breakfast' },
  ],
};

export const DRUG_SUGGESTIONS = [
  'Paracetamol 650mg', 'Ibuprofen 400mg', 'Azithromycin 500mg', 'Amoxicillin 500mg',
  'Cefixime 200mg', 'Metformin 500mg', 'Amlodipine 5mg', 'Telmisartan 40mg',
  'Pantoprazole 40mg', 'Omeprazole 20mg', 'Levocetirizine 5mg', 'Montelukast 10mg',
  'Salbutamol inhaler', 'Budesonide inhaler', 'Atorvastatin 10mg', 'Aspirin 75mg',
  'Clopidogrel 75mg', 'Furosemide 40mg', 'Spironolactone 25mg', 'Insulin Glargine',
];

export const FREQUENCIES = ['OD', 'BD', 'TDS', 'QID', 'SOS', 'HS', 'STAT', 'Weekly'];
export const ROUTES = ['Oral', 'IV', 'IM', 'SC', 'Topical', 'Inhalation', 'Nasal', 'Eye drops'];

export const ICD10_COMMON = [
  { code: 'J06.9', label: 'Acute upper respiratory infection' },
  { code: 'J18.9', label: 'Pneumonia, unspecified' },
  { code: 'I10', label: 'Essential hypertension' },
  { code: 'E11.9', label: 'Type 2 diabetes mellitus' },
  { code: 'K21.0', label: 'GERD with oesophagitis' },
  { code: 'K29.7', label: 'Gastritis, unspecified' },
  { code: 'J45.9', label: 'Asthma, unspecified' },
  { code: 'N39.0', label: 'UTI, site not specified' },
  { code: 'B34.9', label: 'Viral infection, unspecified' },
  { code: 'R50.9', label: 'Fever, unspecified' },
  { code: 'R51', label: 'Headache' },
  { code: 'M79.3', label: 'Myalgia' },
  { code: 'L30.9', label: 'Dermatitis, unspecified' },
  { code: 'E78.5', label: 'Hyperlipidaemia' },
  { code: 'F41.9', label: 'Anxiety disorder' },
];

export const LAB_PANELS = {
  basic: ['CBC', 'ESR', 'CRP'],
  metabolic: ['FBS', 'PPBS', 'HbA1c', 'Lipid profile'],
  renal: ['Urea', 'Creatinine', 'Uric acid', 'Electrolytes'],
  liver: ['LFT (SGOT, SGPT, ALP, Bilirubin)', 'GGT'],
  thyroid: ['TSH', 'Free T4'],
  urine: ['Urine routine', 'Urine culture'],
  imaging: ['X-ray Chest PA', 'USG Abdomen', 'ECG', '2D Echo'],
};

export const LAB_TESTS_FLAT = Object.values(LAB_PANELS).flat();

export const EXAM_SYSTEMS = [
  { id: 'general', label: 'General', normals: 'Alert, oriented, no pallor/icterus/cyanosis' },
  { id: 'cvs', label: 'CVS', normals: 'S1 S2 normal, no murmur' },
  { id: 'rs', label: 'Respiratory', normals: 'Bilateral air entry, no added sounds' },
  { id: 'abdomen', label: 'Abdomen', normals: 'Soft, non-tender, no organomegaly' },
  { id: 'cns', label: 'CNS', normals: 'No focal deficit, reflexes normal' },
  { id: 'ent', label: 'ENT', normals: 'Throat mild congestion, TM normal' },
  { id: 'skin', label: 'Skin', normals: 'No active lesions' },
  { id: 'msk', label: 'MSK', normals: 'No joint swelling, ROM normal' },
];

export const PMH_OPTIONS = [
  'Hypertension', 'Diabetes', 'Asthma', 'COPD', 'CAD', 'CKD', 'Hypothyroid',
  'Epilepsy', 'TB', 'Hepatitis', 'Anaemia',
];

export const emptyRxLine = () => ({
  drug: '', generic: '', itemId: '', dose: '1 tab', m: 1, a: 0, n: 1, food: 'After food',
  frequency: '', duration: '5 days', route: 'Oral', instructions: '',
});

export const emptyVitals = () => ({
  bp: '', pulse: '', temp: '', spo2: '', rr: '', weight: '', height: '', bmi: '',
});

export const emptyHpi = () => ({
  onset: '', duration: '', severity: '', location: '', aggravating: '', relieving: '', associated: '',
});

export const emptyHistory = () => ({
  allergies: '', chronicConditions: [], pmh: [], surgical: '', family: '', social: '', currentMeds: '',
});

export const emptyExam = () =>
  EXAM_SYSTEMS.reduce((acc, s) => ({ ...acc, [s.id]: { finding: '', abnormal: false } }), {});

export const emptyAssessment = () => ({
  primary: '', secondary: [], differential: '', clinicalImpression: '', icd10: '', icdLabel: '',
});

export const emptyPlan = () => ({
  advice: '', followUp: '', referral: '', diet: '', activity: '', warningSigns: '',
});

export const emptySoap = () => ({ subjective: '', objective: '', assessment: '', plan: '' });

export function createEmrForm(overrides = {}) {
  return {
    patientName: '',
    patientId: '',
    patientAge: '',
    patientGender: '',
    patientPhone: '',
    visitType: 'OPD',
    complaint: '',
    hpi: emptyHpi(),
    history: emptyHistory(),
    vitals: emptyVitals(),
    examination: emptyExam(),
    assessment: emptyAssessment(),
    rxLines: [emptyRxLine()],
    labOrders: [],
    labNotes: '',
    plan: emptyPlan(),
    soap: emptySoap(),
    notes: '',
    privateNotes: '',
    healthAssessment: {},
    ophthalmology: {},
    scribeNote: '',
    ...overrides,
  };
}
